// runtime-audio.js — sound-effects engine (Web Audio API, no external files)
// plus dual-phone SFX sync and remote commentary audio. Split from runtime-core.js.
// The audio graph handles (_audioCtx, _audioDest, mic nodes) live in store-runtime.js
// because the camera and recording modules also need them.
import { get } from 'svelte/store';
import { peerRole } from './svelte/store.js';
import { PeerSync } from './peer-sync.js';
import { appSettings, saveSettings } from './store.js';
import {
  _audioCtx, _audioDest, _micGainNode,
  _setAudioCtx, _setAudioDest, _setMicGainNode, _setAudioKeepAlive
} from './store-runtime.js';

// src/audio.js — Sound Effects Engine (Web Audio API)
// Extracted from js for modularity


// ─── DUAL-PHONE SFX SYNC ────────────────────────────────────────────────
export function _peerSyncSfx(name) {
  if (get(peerRole) === "scorer" && typeof PeerSync !== "undefined" && PeerSync.isConnected) {
    try { PeerSync.sendSfx(name); } catch (_) {}
  }
}

// ─── SOUND EFFECTS ENGINE (Web Audio API - no external files) ──────────
// ─── SOUND EFFECTS ENGINE (Web Audio API — no external files) ──────────

export let getAudioCtx = function() {
  if (!_audioCtx) {
    // Match the mic's native sample rate (48 kHz on Android Chrome) so the mic isn't
    // resampled twice (mic → AudioContext → MediaRecorder).
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) { console.warn('Web Audio not supported'); return; }
    try { _setAudioCtx(new Ctor({ sampleRate: 48000 })); }
    catch (_) { _setAudioCtx(new Ctor()); }
    _setAudioDest(_audioCtx.createMediaStreamDestination());
    // Pre-create the mic gain node so SFX still get mixed even before the mic connects.
    _setMicGainNode(_audioCtx.createGain());
    // Clamp against corrupt localStorage — gain.value of e.g. 999 would blow speakers.
    _micGainNode.gain.value = Math.max(0, Math.min(10, Number(appSettings.micGain) || 1));
    _micGainNode.connect(_audioDest);

    // ── SILENT KEEP-ALIVE ──────────────────────────────────────────────
    // The recorded audio track comes from _audioDest. On many Android phones the
    // AudioContext would go idle / the destination track would stop emitting
    // samples a few seconds into a recording → the classic "audio works for the
    // first few balls then goes silent" bug. A permanently-running (inaudible)
    // ConstantSourceNode keeps the graph producing samples for the whole match.
    try {
      const keepAlive = _audioCtx.createConstantSource();
      const kaGain = _audioCtx.createGain();
      kaGain.gain.value = 0.0001; // sub-audible, just enough to keep the node pumping
      keepAlive.connect(kaGain);
      kaGain.connect(_audioDest);
      keepAlive.start();
      _setAudioKeepAlive(keepAlive);
    } catch (_) {}
  }
  if (_audioCtx.state === "suspended") { try { _audioCtx.resume(); } catch (_) {} }
  return _audioCtx;
}

// ─── MICROPHONE → RECORDING MIX ─────────────────────────────────────────
// _micGainNode is created in getAudioCtx() and connected to _audioDest, but
// NOTHING was ever connected INTO it — the camera's microphone track was
// captured by getUserMedia and then simply never routed into the audio graph.
// Because _audioDest always yields an audio track (even when only the silent
// keep-alive is connected), createCompositeRecordingStream's
// `mixedAudioTrack || cameraAudioTrack` fallback never fired either.
// Net result: every saved video had a technically-valid but SILENT audio track.
let _micSourceNode = null;
let _micSourceStream = null;

export function connectMicToGraph(stream) {
  const track = stream?.getAudioTracks?.()?.[0];
  if (!track) return false;
  if (_micSourceStream === stream && _micSourceNode) return true; // already wired
  try {
    const ctx = getAudioCtx();
    disconnectMicFromGraph();
    _micSourceNode = ctx.createMediaStreamSource(stream);
    // ONLY into the recording mix. Never connect the mic to ctx.destination —
    // that would play the microphone back out of the phone's own speaker and
    // feed back into itself.
    _micSourceNode.connect(_micGainNode);
    _micSourceStream = stream;
    return true;
  } catch (err) {
    console.warn('[Audio] Could not route the microphone into the recording mix:', err);
    disconnectMicFromGraph();
    return false;
  }
}

export function disconnectMicFromGraph() {
  try { _micSourceNode?.disconnect(); } catch (_) {}
  _micSourceNode = null;
  _micSourceStream = null;
}

export function isMicWiredToGraph() {
  return !!_micSourceNode;
}

export let setMicGain = function(value) {
  appSettings.micGain = Math.max(0, Math.min(10, value));
  saveSettings();
  if (_micGainNode) {
    try { _micGainNode.gain.setTargetAtTime(appSettings.micGain, _audioCtx.currentTime, 0.05); } catch (_) {}
  }
}

let playTone = function(freq, duration, type, volume) {
  if (!appSettings.sfxEnabled) return;
  try {
    const ctx = getAudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type || "sine";
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(volume || 0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
    osc.connect(gain);
    gain.connect(ctx.destination);
    if (_audioDest) gain.connect(_audioDest);
    osc.start();
    osc.stop(ctx.currentTime + duration);
  } catch (e) { /* audio not available */ }
}

export let sfxFour = function() {
  if (appSettings.sfxBoundaryEnabled === false) return;
  // Rich ascending fanfare with harmonics + crowd cheer
  playTone(523, 0.12, "square", 0.2);
  playTone(528, 0.12, "sawtooth", 0.08); // detune for thickness
  setTimeout(() => playTone(659, 0.12, "square", 0.2), 80);
  setTimeout(() => playTone(784, 0.18, "square", 0.25), 160);
  setTimeout(() => playTone(1047, 0.25, "sine", 0.15), 240);
  // Subtle crowd burst
  setTimeout(() => sfxCrowdBurst(1.2), 200);
  _peerSyncSfx("sfxFour");
}

export let sfxSix = function() {
  if (appSettings.sfxBoundaryEnabled === false) return;
  // Dramatic ascending fanfare + bass drop + crowd explosion
  playTone(220, 0.15, "sine", 0.3); // bass thump
  setTimeout(() => {
    playTone(523, 0.08, "sawtooth", 0.18);
    playTone(659, 0.08, "sawtooth", 0.18);
  }, 80);
  setTimeout(() => {
    playTone(784, 0.08, "sawtooth", 0.2);
    playTone(1047, 0.08, "sawtooth", 0.2);
  }, 160);
  setTimeout(() => {
    playTone(1319, 0.35, "sawtooth", 0.3);
    playTone(1568, 0.35, "sine", 0.12);
  }, 240);
  setTimeout(() => sfxCrowdBurst(1.8), 200);
  _peerSyncSfx("sfxSix");
}

export function sfxWicket() {
  if (appSettings.sfxWicketEnabled === false) return;
  // Dramatic descending stinger with reverb tail
  playTone(880, 0.08, "square", 0.3);
  playTone(440, 0.15, "square", 0.3);
  setTimeout(() => playTone(330, 0.2, "square", 0.25), 120);
  setTimeout(() => playTone(220, 0.4, "sawtooth", 0.2), 240);
  setTimeout(() => playTone(110, 0.6, "sine", 0.15), 360);
  _peerSyncSfx("sfxWicket");
}

export function sfxCrowdBurst(durationSec) {
  if (!appSettings.sfxEnabled) return;
  try {
    const ctx = getAudioCtx();
    const dur = durationSec || 1.5;
    const bufSz = Math.floor(ctx.sampleRate * dur);
    const buf = ctx.createBuffer(1, bufSz, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < bufSz; i++) d[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource(); src.buffer = buf;
    const flt = ctx.createBiquadFilter(); flt.type = "bandpass"; flt.frequency.value = 800; flt.Q.value = 0.7;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.01, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.35, ctx.currentTime + 0.15);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur);
    src.connect(flt); flt.connect(g); g.connect(ctx.destination);
    if (_audioDest) g.connect(_audioDest);
    src.start();
  } catch(_) {}
}

export function sfxCheer() {
  if (appSettings.sfxBoundaryEnabled === false) return;
  sfxCrowdBurst(1.5);
  _peerSyncSfx("sfxCheer");
}

export let sfxHorn = function() {
  if (!appSettings.sfxEnabled || appSettings.sfxHornEnabled === false) return;
  try {
    const ctx = getAudioCtx();
    const dur = 1.5;
    // Thick dissonant square wave cluster for the classic IPL Airhorn
    const freqs = [280, 285, 292, 420];
    freqs.forEach(f => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "square";
      osc.frequency.setValueAtTime(f, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(f * 0.9, ctx.currentTime + dur);
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.setValueAtTime(0.15, ctx.currentTime + dur * 0.7);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur);
      osc.connect(gain);
      gain.connect(ctx.destination);
      if (_audioDest) gain.connect(_audioDest);
      osc.start();
      osc.stop(ctx.currentTime + dur);
    });
  } catch(e) {}
  _peerSyncSfx("sfxHorn");
}

let sfxSiren = function() {
  if (!appSettings.sfxEnabled || appSettings.sfxHornEnabled === false) return;
  // Advanced IPL style Siren (Wailing)
  try {
    const ctx = getAudioCtx();
    const dur = 3.0;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sawtooth';
    
    osc.frequency.setValueAtTime(400, ctx.currentTime);
    osc.frequency.linearRampToValueAtTime(800, ctx.currentTime + 0.5);
    osc.frequency.linearRampToValueAtTime(400, ctx.currentTime + 1.0);
    osc.frequency.linearRampToValueAtTime(800, ctx.currentTime + 1.5);
    osc.frequency.linearRampToValueAtTime(400, ctx.currentTime + 2.0);
    osc.frequency.linearRampToValueAtTime(800, ctx.currentTime + 2.5);
    osc.frequency.linearRampToValueAtTime(400, ctx.currentTime + 3.0);
    
    gain.gain.setValueAtTime(0.001, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.3, ctx.currentTime + 0.2);
    gain.gain.setValueAtTime(0.3, ctx.currentTime + 2.5);
    gain.gain.linearRampToValueAtTime(0.001, ctx.currentTime + dur);
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    if (_audioDest) gain.connect(_audioDest);
    
    osc.start();
    osc.stop(ctx.currentTime + dur);
  } catch(e) {}
  _peerSyncSfx("sfxSiren");
}

export function sfxMilestone() {
  [523, 659, 784, 1047].forEach((f, i) => {
    setTimeout(() => playTone(f, 0.2, "sine", 0.25), i * 120);
  });
  _peerSyncSfx("sfxMilestone");
}

export function sfxDot() {
  if (appSettings.sfxBoundaryEnabled === false) return;
  playTone(200, 0.08, "sine", 0.1);
  _peerSyncSfx("sfxDot");
}

const _origSfxFour = sfxFour;
const _origSfxSix = sfxSix;
const _origSfxWicket = sfxWicket;
let _remoteAudioSource = null;
let _remoteAudioGain = null;

export function playRemoteSfx(fx) {
  if (fx === "sfxFour") _origSfxFour();
  else if (fx === "sfxSix") _origSfxSix();
  else if (fx === "sfxWicket") _origSfxWicket();
  else if (fx === "sfxHorn") sfxHorn();
  else if (fx === "sfxCheer") sfxCheer();
  else if (fx === "sfxSiren") sfxSiren();
  else if (fx === "sfxMilestone") sfxMilestone();
  else if (fx === "sfxDot") sfxDot();
}

export function attachRemoteAudioStream(remoteStream, gainValue = 1) {
  const ctx = getAudioCtx();
  if (_remoteAudioSource) _remoteAudioSource.disconnect();
  if (_remoteAudioGain) _remoteAudioGain.disconnect();
  const source = ctx.createMediaStreamSource(remoteStream);
  const gain = ctx.createGain();
  gain.gain.value = Math.max(0, Math.min(10, Number(gainValue) || 1));
  source.connect(gain);
  if (_audioDest) gain.connect(_audioDest);
  _remoteAudioSource = source;
  _remoteAudioGain = gain;
}

// ONE element + ONE node pair for the whole session.
let _commentaryEl = null;
let _commentaryGain = null;

// Chrome/Android populate the voice list ASYNCHRONOUSLY — getVoices() returns []
// until the `voiceschanged` event fires. Without this cache the first few balls
// of every session got the OS default voice instead of the Hindi/English one.
let _ttsVoices = [];
function _refreshTtsVoices() {
  try { _ttsVoices = window.speechSynthesis.getVoices() || []; } catch (_) { _ttsVoices = []; }
}
if (typeof window !== 'undefined' && window.speechSynthesis) {
  _refreshTtsVoices();
  try { window.speechSynthesis.addEventListener('voiceschanged', _refreshTtsVoices); } catch (_) {}
}

// NOTE: Web Speech output is produced by the OS speech engine and cannot be
// routed into the WebAudio graph, so TTS commentary is heard live on the phone
// but is NOT mixed into the recorded video. Only the MP3 path reaches _audioDest.
function speakCommentaryFallback(text) {
  if (typeof window === 'undefined' || !window.speechSynthesis || !text) return;
  try {
    window.speechSynthesis.cancel();
    const cleanText = String(text)
      .replace(/^(Aakash Chopra|Ravi Shastri|Sidhu) style:\s*/i, '');

    const utterance = new SpeechSynthesisUtterance(cleanText);
    utterance.rate = 1.05;
    utterance.pitch = 1.0;

    if (!_ttsVoices.length) _refreshTtsVoices();
    const isHindiText = /[ऀ-ॿ]|behtareen|chakka|chauka|maara|gagan|killiyan|khataak/i.test(cleanText);
    // Set lang too: when no matching voice object is available the engine can
    // still pick a sensible one from the language tag alone.
    utterance.lang = isHindiText ? 'hi-IN' : 'en-IN';
    const preferredVoice =
      _ttsVoices.find(v => v.lang && v.lang.toLowerCase().startsWith(isHindiText ? 'hi' : 'en-in')) ||
      _ttsVoices.find(v => v.lang && v.lang.toLowerCase().startsWith(isHindiText ? 'hi' : 'en'));
    if (preferredVoice) utterance.voice = preferredVoice;

    window.speechSynthesis.speak(utterance);
  } catch (_) {}
}

export function playCommentaryAudio(audioPath, fallbackText = '') {
  if (!appSettings.sfxEnabled || appSettings.aiCommentaryAudioEnabled === false) return;
  try {
    const ctx = getAudioCtx();
    if (ctx && ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }

    if (audioPath) {
      if (!_commentaryEl) {
        _commentaryEl = new Audio();
        _commentaryEl.preload = "auto";
        _commentaryEl.addEventListener("error", () => {
          console.warn("[Commentary] audio asset unavailable:", _commentaryEl?.currentSrc || audioPath);
          if (fallbackText) speakCommentaryFallback(fallbackText);
        });
        const source = ctx.createMediaElementSource(_commentaryEl);
        _commentaryGain = ctx.createGain();
        _commentaryGain.gain.value = 1.0;
        source.connect(_commentaryGain);
        _commentaryGain.connect(ctx.destination);
        if (_audioDest) _commentaryGain.connect(_audioDest);
      }
      _commentaryEl.pause();
      _commentaryEl.src = audioPath;
      const played = _commentaryEl.play();
      if (played && typeof played.catch === "function") {
        played.catch(err => {
          if (err.name === 'AbortError') return;
          console.warn("Commentary playback skipped:", err?.message || err);
          if (fallbackText) speakCommentaryFallback(fallbackText);
        });
      }
    } else if (fallbackText) {
      speakCommentaryFallback(fallbackText);
    }
  } catch (err) {
    console.warn("Failed to play commentary audio:", err);
    if (fallbackText) speakCommentaryFallback(fallbackText);
  }
}

